/**
 * @file: FullCalendar events in the format the shared on-screen module needs.
 *
 * Two surfaces show the same result from the same events. The surfaces are the
 * strip below the grid and the ribbon in the grid. Both surfaces use this
 * mapping. Two copies of the mapping can disagree about which field holds the
 * priority. The two surfaces then show different results on the same screen.
 *
 * The same argument applies to two more rules. These rules are how the code
 * reads a selection, and how it compares two interval ids. Both surfaces mark
 * the same selection, so the rules are in this file with the mapping.
 *
 * The panel reads the same events for the band peers of an interval, and for
 * whether a save puts the interval on screen. These rules are in this file
 * too.
 */

import { asMs, overlapsInTime, segmentsForRange, winnersOf } from '../../../shared/on-screen-segments'
import { BAND_PLACEMENT, ROLE_BANDS, placementInBand, priorityOnSave, raiseAbovePeers } from '../../../shared/priority-bands'

/**
 * This value is the smallest height of a run on the ribbon, in pixels.
 *
 * A stretch that has a winner must be visible. The ribbon dropped a run below
 * one pixel, so at the widest zoom any stretch under two and a half minutes
 * vanished, and the gap then said that nothing reaches the screen there. A gap
 * on these surfaces means no winner, so an invisible run is not a small
 * inaccuracy but the opposite statement.
 *
 * The value is two pixels and not one. The ribbon is six pixels wide, so two
 * pixels are in proportion. One pixel can disappear on a scaled display.
 *
 * This is not the floor FullCalendar puts under a bar. eventMinHeight keeps a
 * short bar large enough to click, which is a different question with a
 * different answer.
 */
export const MIN_VISIBLE_RUN_PX = 2

/**
 * @param {Object} calendar - A rendered FullCalendar instance
 * @returns {Array} [{ id, title, playlistId, colour, start, end, priority, role }]. The function
 *   ignores an event that has no start, no end, or no extended props. Such an
 *   event does not give the times that the on-screen calculation needs.
 */
export function intervalsOf (calendar) {
  if (!calendar || typeof calendar.getEvents !== 'function') {
    return []
  }

  return calendar.getEvents()
    .filter(event => event.start && event.end && event.extendedProps)
    .map(event => ({
      id: event.extendedProps.intervalId,
      title: event.title,
      playlistId: event.extendedProps.sceneCollectionId,
      colour: event.backgroundColor,
      start: event.start,
      end: event.end,
      priority: event.extendedProps.priority,
      role: event.extendedProps.role
    }))
}

/**
 * Compare two interval ids.
 *
 * The server sends an id as a number or as a string. The comparison therefore
 * converts both ids with Number. A null or an undefined id identifies no
 * interval, and matches no interval.
 */
export function sameIntervalId (one, other) {
  if (one === null || one === undefined || other === null || other === undefined) {
    return false
  }

  return Number(one) === Number(other)
}

/**
 * Give the id of the stored interval that the panel shows, or null.
 *
 * The result is also null when the panel shows a new interval. A new interval
 * is not saved yet. It therefore has no part of the result on screen, and the
 * surfaces have nothing to mark.
 */
export function inspectedIntervalId (controller) {
  const inspecting = controller && controller.stateManager && controller.stateManager.inspecting
  const id = inspecting ? inspecting.intervalId : null

  return id === null || id === undefined ? null : id
}

/**
 * This value tells how much of its run an interval wins after a save. The
 * panel says one of three sentences, so the value has three forms.
 */
export const SCREEN_SHARE = Object.freeze({ WHOLE: 'whole', PART: 'part', NEVER: 'never' })

/**
 * This value is the standing of an interval that the panel cannot calculate.
 * The panel then shows no shared stretch and no screen answer.
 *
 * The state manager adds the role to a copy, so a reader cannot compare with
 * this object. A reader tests the known field. False means that the overlaps
 * are unknown, and not that there are none.
 */
export const NO_STANDING = Object.freeze({
  peers: Object.freeze([]),
  under: Object.freeze([]),
  over: Object.freeze([]),
  screen: null,
  refusal: null,
  raiseGainsScreen: false,
  known: false
})

/** Give a calculated standing. The fields that the caller does not give have the NO_STANDING value. */
const standingWith = fields => Object.assign({}, NO_STANDING, fields, { known: true })

const spanOf = interval => ({ start: asMs(interval.start), end: asMs(interval.end) })

/**
 * Tell whether the range holds the whole run.
 *
 * The grid fetches the intervals of the range on show. An interval outside
 * that range is not in the calendar. When the run stays inside the range, the
 * calendar holds every interval that overlaps the run. Otherwise the panel
 * does not know all the overlaps. A run across Sunday midnight is an example.
 *
 * @param {Object|null} range - { start, end } of the view. The end is exclusive
 * @param {Object} run - { start, end }
 * @returns {boolean} False when there is no range or no run
 */
export function rangeHoldsRun (range, run) {
  if (!range || !run.start || !run.end) {
    return false
  }

  const held = spanOf(range)
  const span = spanOf(run)

  return span.start >= held.start && span.end <= held.end
}

/**
 * Give the run of the candidate in milliseconds, or null.
 *
 * The operator can empty a field, or type an end before the start. Such times
 * have no run.
 */
function runOf (candidate) {
  if (!candidate.start || !candidate.end) {
    return null
  }

  const run = spanOf(candidate)

  return run.end > run.start ? run : null
}

/**
 * Give the pieces of the run with their winners, with the outcome of
 * priorityOnSave. A refusal writes nothing, so it wins no piece.
 */
function piecesAfterSave (intervals, candidate, run, { priority, refusal }) {
  if (refusal) {
    return []
  }

  const saved = intervals.map(interval => sameIntervalId(interval.id, candidate.id)
    ? Object.assign({}, interval, { start: candidate.start, end: candidate.end, priority })
    : interval)

  return segmentsForRange(saved, run.start, run.end)
}

const winsPiece = (candidate, piece) => Boolean(piece.winner) && sameIntervalId(piece.winner.id, candidate.id)

/** Give the milliseconds of the pieces that the candidate wins. */
function screenTimeOf (pieces, candidate) {
  return pieces
    .filter(piece => winsPiece(candidate, piece))
    .reduce((total, piece) => total + piece.end - piece.start, 0)
}

/**
 * Give how much of the run the candidate wins, and the titles of the intervals
 * that win the other pieces. A piece with no winner adds no title.
 *
 * The playlist ids of those intervals are a separate set. Two playlists can
 * have one name, so the panel reads the names for the text and the ids for
 * the test of its own playlist. The Preflight report has the same two lists.
 */
function screenOf (pieces, candidate, run) {
  const won = screenTimeOf(pieces, candidate)
  const share = won === 0 ? SCREEN_SHARE.NEVER : won < run.end - run.start ? SCREEN_SHARE.PART : SCREEN_SHARE.WHOLE
  const others = winnersOf(pieces).filter(winner => !sameIntervalId(winner.id, candidate.id))

  return {
    share,
    coveredBy: [...new Set(others.map(winner => winner.title))],
    coveredByPlaylistIds: [...new Set(others.map(winner => winner.playlistId))]
  }
}

/**
 * Sort the overlaps of the candidate by band.
 *
 * A peer has a priority in the band of the candidate. An interval under the
 * candidate has a priority below that band, and an interval over it has a
 * priority above it. The role of an overlap does not decide. The server
 * derives that role from the priority, so the two give the same peers.
 *
 * The override band and a value from before the roles have no band in
 * ROLE_BANDS. Such a candidate gets only the overlaps with the same role.
 */
function layersOf (overlaps, role) {
  const band = ROLE_BANDS[role]

  if (band === undefined) {
    return { peers: role ? overlaps.filter(overlap => overlap.role === role) : [], under: [], over: [] }
  }

  const placed = placement => overlaps.filter(overlap => placementInBand(overlap.priority, band) === placement)

  return {
    peers: placed(BAND_PLACEMENT.WITHIN),
    under: placed(BAND_PLACEMENT.UNDER),
    over: placed(BAND_PLACEMENT.OVER)
  }
}

/**
 * Sort the overlaps of a new candidate as its first save leaves them.
 *
 * The server gives a new interval the value of raiseAbovePeers. A peer below
 * that value is under the new interval after the save. A refused raise writes
 * nothing, so the peers stay peers.
 */
function layersAfterCreate (overlaps, role) {
  const layers = layersOf(overlaps, role)

  if (ROLE_BANDS[role] === undefined) {
    return layers
  }

  const { priority } = raiseAbovePeers(role, overlaps.map(overlap => overlap.priority))
  const beaten = layers.peers.filter(peer => Number(peer.priority) < priority)

  return {
    peers: layers.peers.filter(peer => !beaten.includes(peer)),
    under: layers.under.concat(beaten),
    over: layers.over
  }
}

/** Give the layers as lists of { title }. The panel needs only the names. */
function titledLayers ({ peers, under, over }) {
  const titled = list => list.map(overlap => ({ title: overlap.title }))

  return { peers: titled(peers), under: titled(under), over: titled(over) }
}

/**
 * Give the band peers of the candidate, and how much of it a save puts on
 * screen. Also tell whether a raise above those peers puts more of it on
 * screen.
 *
 * The candidate has the times and the role that the panel shows. The server
 * compares those values when it saves, and not the stored row.
 *
 * A peer has the same role, and therefore the same band. A peer also overlaps
 * the candidate in time. Two intervals that touch end to end do not overlap,
 * because they are never on screen at the same moment. A value from before the
 * bands is in no band, so it has no peers.
 *
 * The screen answer uses the winner rule of the strip and the ribbon. The
 * panel thus cannot contradict the two surfaces beside it. The priority comes
 * from priorityOnSave, the rule that addInterval applies. The server reads the
 * overlaps from the database, and the calendar holds the same rows for the
 * week on show.
 *
 * The raise is compared by screen time, and not by a yes or a no. An interval
 * can win part of its run and lose the stretch it shares with a newer peer.
 * The raise then wins that stretch. A raise changes only the order in the
 * band, so a higher band can still cover the shared stretch. The raise then
 * gains nothing.
 *
 * A new interval has no stored row and no stored priority. It gets only the
 * overlaps, so the panel can say what its role changes. Its peers are under it
 * when the server can raise it above them.
 *
 * A save that the server refuses writes nothing, so the screen does not
 * change. The standing then gives the refusal and no screen answer. A screen
 * answer of "never" was false for an interval that stays on screen.
 *
 * @param {Array} intervals - From intervalsOf
 * @param {Object} candidate - { id, role, start, end }. A new interval has no id
 * @returns {Object|null} { peers, under, over, screen, refusal,
 *   raiseGainsScreen, known }. The first three are lists of { title }. The screen is
 *   { share, coveredBy, coveredByPlaylistIds }. The share is a SCREEN_SHARE
 *   value. coveredBy has the titles of the intervals that win the rest of the
 *   run, and coveredByPlaylistIds has their playlist ids. The refusal is the
 *   reason the server refuses a plain save, or null. The result is NO_STANDING
 *   when the times have no run. It is null when the calendar does not hold the
 *   stored row, for example in another week. The answer is then unknown, and
 *   not a refusal.
 */
export function screenStandingAmong (intervals, candidate) {
  const run = runOf(candidate)

  if (!run) {
    return NO_STANDING
  }

  const overlaps = intervals.filter(interval =>
    !sameIntervalId(interval.id, candidate.id) && overlapsInTime(spanOf(interval), run))
  if (candidate.id === null || candidate.id === undefined) {
    return standingWith(titledLayers(layersAfterCreate(overlaps, candidate.role)))
  }

  const layers = titledLayers(layersOf(overlaps, candidate.role))

  const stored = intervals.find(interval => sameIntervalId(interval.id, candidate.id))

  if (!stored) {
    return null
  }

  const takenByOverlaps = overlaps.map(overlap => overlap.priority)
  const outcomeWith = winsPeers => priorityOnSave(stored.priority, { role: candidate.role, winsPeers }, takenByOverlaps)
  const piecesWith = winsPeers => piecesAfterSave(intervals, candidate, run, outcomeWith(winsPeers))
  const { refusal } = outcomeWith(false)

  if (refusal) {
    return standingWith(Object.assign({}, layers, { refusal }))
  }

  const plainSave = piecesWith(false)

  return standingWith(Object.assign({}, layers, {
    screen: screenOf(plainSave, candidate, run),
    // Without a peer there is nothing to raise above.
    raiseGainsScreen: layers.peers.length > 0 && screenTimeOf(piecesWith(true), candidate) > screenTimeOf(plainSave, candidate)
  }))
}
